// Pure helpers for the Insights page (mockup docs/design/mockups/_build/pages/14-insights.html).
import { terms as accountTerms } from '../../design/terms';

export const MAX_MATRIX_ROWS = 12;

const sumHomes = (ids, communitiesById) => ids.reduce((sum, id) => sum + (communitiesById[id]?.homeCount || 0), 0);

const collectCommunityIds = (node) => (node.communityIds || []).concat(...(node.children || []).map(collectCommunityIds));

/**
 * Matrix columns for the global filter: the top-level nodes (Leland: Divisions) when nothing is selected, the
 * communities of the selected node, or the selected communities. groupBy is the insights/concerns parameter.
 */
export function matrixColumns({ topNodes, nodesById, communitiesById, filter, levels, terms = accountTerms() }) {
  if (filter.communityIds.length) {
    return {
      groupBy: 'community',
      title: filter.communityIds.length === 1 ? `Concerns in ${communitiesById[filter.communityIds[0]]?.name || `this ${terms.communityLower}`}` : `Concerns by ${terms.communityLower}`,
      columns: filter.communityIds.map((id) => ({ id, name: communitiesById[id]?.name || terms.community, homes: communitiesById[id]?.homeCount || 0 })),
    };
  }
  if (filter.nodeId && nodesById[filter.nodeId]) {
    const node = nodesById[filter.nodeId];
    return {
      groupBy: 'community',
      title: `Concerns by ${terms.communityLower} in ${node.name}`,
      columns: collectCommunityIds(node)
        .map((id) => communitiesById[id])
        .filter(Boolean)
        .map((c) => ({ id: c.id, name: c.name, homes: c.homeCount || 0 })),
    };
  }
  const top = levels[0];
  return {
    groupBy: top ? `hierarchyLevel:${top.id}` : 'community',
    title: `Concerns by ${top?.displayName || 'Division'}`,
    columns: topNodes.map((n) => ({ id: n.id, name: n.name, homes: sumHomes(collectCommunityIds(n), communitiesById) })),
  };
}

/**
 * Pivots insights/concerns cells into rows (categories, most calls first, at most MAX_MATRIX_ROWS) over the columns.
 * A group in the response that is not a column (for example "Not assigned") is added as a column so totals reconcile.
 */
export function pivotConcerns(cells, columns) {
  const cols = [...columns];
  cells.forEach((c) => {
    if (!cols.some((col) => col.id === c.groupId)) cols.push({ id: c.groupId, name: c.groupName || 'Not assigned', homes: 0 });
  });
  const byCategory = new Map();
  cells.forEach((c) => {
    const row = byCategory.get(c.categoryId) || { categoryId: c.categoryId, name: c.categoryName, severity: c.highestSeverity, values: {}, total: 0 };
    row.values[c.groupId] = (row.values[c.groupId] || 0) + c.callCount;
    row.total += c.callCount;
    byCategory.set(c.categoryId, row);
  });
  const rows = [...byCategory.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name)).slice(0, MAX_MATRIX_ROWS);
  return { columns: cols, rows };
}

export const perThousandHomes = (value, homes) => (homes > 0 ? (value / homes) * 1000 : 0);

/** Monday on or before the ISO date (yyyy-mm-dd), as an ISO date. */
export function mondayOf(isoDate) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

/** From/to for the weekly sentiment chart: eight weeks ending with the week of `to`. */
export function sentimentWindow(to, weeks = 8) {
  const monday = new Date(`${mondayOf(to)}T00:00:00Z`);
  monday.setUTCDate(monday.getUTCDate() - (weeks - 1) * 7);
  return { from: monday.toISOString().slice(0, 10), to };
}

const NEGATIVE = ['Negative', 'VeryNegative'];
const POSITIVE = ['Positive', 'VeryPositive'];

/** Percent shares [negative, mixed, neutral, positive] of one week's caller sentiment. */
export function sentimentShares(distribution = {}) {
  const count = (keys) => keys.reduce((sum, k) => sum + (distribution[k] || 0), 0);
  const values = [count(NEGATIVE), count(['Mixed']), count(['Neutral']), count(POSITIVE)];
  const total = values.reduce((a, b) => a + b, 0);
  return total ? values.map((v) => Math.round((v / total) * 100)) : [0, 0, 0, 0];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const weekLabel = (periodStart) => {
  const d = new Date(`${String(periodStart).slice(0, 10)}T00:00:00Z`);
  return `Week of ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

/** "legal, escalation, insurance": the first word of each category, lower case (mockup KPI meta). */
/** "legal, escalation, insurance": first words of the top categories, as many as fit one KPI caption line (28 characters). */
export function shortCategoryList(names, maxLength = 28) {
  const words = [];
  (names || []).forEach((n) => {
    const word = n.split(' ')[0].toLowerCase();
    if (words.includes(word)) return;
    if ([...words, word].join(', ').length <= maxLength || words.length === 0) words.push(word);
  });
  return words.join(', ');
}

/** "emergencies this week" (the Immediate KPI counts Immediate classifications, emergencies by definition). */
export const emergencyCaption = (count, period) => `${count === 1 ? 'emergency' : 'emergencies'} ${period}`;

export const periodPhrase = (presetId) => ({ today: 'today', yesterday: 'yesterday', last7: 'this week' }[presetId] || 'in this period');
